/**
 * components/month/MonthBar.tsx
 * Month selector with monthly summary and close/open actions.
 * - Filters tables when toggle is enabled (prop onFilterChange).
 * - Hardened against invalid month values and avoided toISOString in render deps.
 * - Updated: recompute summaries on active store change.
 * - Role-aware:
 *   - entry users: cannot navigate months or close/open, KPIs hidden.
 *   - viewer: KPIs visible; controls remain disabled (read-only).
 */

import { useEffect, useMemo, useState } from 'react'
import { useSelectedMonth, formatMonth } from '../../hooks/useMonth'
import { useAccountingStore } from '../../store/accounting'
import { formatCurrency, formatDate } from '../../lib/format'
import { CalendarDays, Lock, Unlock, ChevronRight, ChevronLeft, Info } from 'lucide-react'
import { useBoolPref } from '../../hooks/usePref'
import { useAuthStore } from '../../store/auth'
import { useUserStore } from '@/store/user'

interface MonthBarProps {
  onFilterChange: (range: { start: Date; end: Date } | null) => void
}

function isValidDate(d: Date) {
  return d instanceof Date && !Number.isNaN(d.getTime())
}

/**
 * MonthBar
 * Provides month selection, monthly KPIs, and close/open month controls.
 * Role rules:
 *  - admin: full controls
 *  - viewer: KPIs visible but controls disabled
 *  - entry: cannot navigate months (forced to current), cannot close/open, KPIs hidden
 */
export function MonthBar({ onFilterChange }: MonthBarProps) {
  const { month, setMonth, range, next, prev } = useSelectedMonth()
  /* Transactions */
  const transactions = useUserStore((s) => s.transactions)
  const expenses = useUserStore((s) => s.expenses)
  //const computeMonthlySummary = useAccountingStore((s) => s.computeMonthlySummary)
  const computeMonthlySummary = useUserStore((s) => s.computeMonthlySummary)
  const activeStoreId = useUserStore((s) => s.activeStoreId)

  const closeMonth = useAccountingStore((s) => s.closeMonth)
  const reopenMonth = useAccountingStore((s) => s.reopenMonth)
  const getClosure = useAccountingStore((s) => s.getClosure)
  //const activeStoreId = useAccountingStore((s) => s.activeStoreId)

  const [applyFilter, setApplyFilter] = useBoolPref('applyMonthFilter', true)
  const [note, setNote] = useState('')

  const currentUser = useAuthStore((s) => s.getCurrentUser())
  const role = currentUser?.role || 'entry'
  const isAdmin = role === 'admin'
  const isViewer = role === 'viewer'

  // Force non-admin users to current month and disable navigation
  useEffect(() => {
    if (!isAdmin) {
      const cur = formatMonth(new Date())
      if (month !== cur) setMonth(cur)
    }

    console.log("Wak");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, month])

  const totals = useMemo(() => {
    console.log("Recompute totals");
    return computeMonthlySummary(month)
  }, [computeMonthlySummary, month, transactions, expenses])
  const closure = useMemo(() => getClosure(month), [getClosure, month, activeStoreId])

  // Notify parent about filter range safely.
  useEffect(() => {
    if (applyFilter && isValidDate(range.start) && isValidDate(range.end)) {
      onFilterChange({ start: range.start, end: range.end })
    } else {
      onFilterChange(null)
    }
    // refresh when switching stores or month
  }, [applyFilter, month, activeStoreId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onClose = () => {
    if (!isAdmin) return
    closeMonth(month, note || undefined)
    setNote('')
  }

  const onOpen = () => {
    if (!isAdmin) return
    reopenMonth(month)
  }

  const monthLabel = new Date(range.start).toLocaleDateString('ar', {
    calendar: 'gregory',
    year: 'numeric',
    month: 'long',
  })

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" dir="rtl">
      {/* Header: selector and controls */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-sky-600" />
          <div className="flex items-center gap-2">
            <button
              onClick={isAdmin ? prev : undefined}
              disabled={!isAdmin}
              className="rounded-lg border border-slate-300 bg-white p-1 text-slate-700 disabled:opacity-50 hover:bg-slate-50"
              title="الشهر السابق"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <input
              type="month"
              value={month}
              onChange={(e) => (isAdmin ? setMonth(e.target.value) : undefined)}
              disabled={!isAdmin}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-50"
              aria-label="اختيار الشهر"
              title="اختر شهرًا ميلاديًا"
            />
            <button
              onClick={isAdmin ? next : undefined}
              disabled={!isAdmin}
              className="rounded-lg border border-slate-300 bg-white p-1 text-slate-700 disabled:opacity-50 hover:bg-slate-50"
              title="الشهر التالي"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-600" title="الشهر المحدد ميلادياً">
              {monthLabel}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={applyFilter}
              onChange={(e) => setApplyFilter(e.target.checked)}
            />
            تطبيق الفلترة على الجداول
          </label>

          {isAdmin && (closure ? (
            <button
              onClick={onOpen}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-white hover:bg-amber-700"
              title={`فُتِح: ${formatDate(closure.closedAt)}`}
            >
              <Unlock className="h-4 w-4" /> إلغاء الإقفال
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ملاحظة الإقفال (اختياري)"
                className="w-48 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-white hover:bg-slate-800"
                title="حفظ لقطة لقيم الشهر الحالية"
              >
                <Lock className="h-4 w-4" /> إقفال الشهر
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* KPIs and closure info */}
      {(isAdmin || isViewer) && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div
            className="rounded-xl border border-slate-200 bg-white p-3"
            title="المبالغ المودعة فعلياً خلال الشهر (تاريخ الحدث)"
          >
            <div className="text-xs text-slate-500">المبيعات المودعة</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{formatCurrency(totals.salesDeposited)}</div>
          </div>

          <div
            className="rounded-xl border border-slate-200 bg-white p-3"
            title="المبالغ المدفوعة فعلياً خلال الشهر (تاريخ الحدث)"
          >
            <div className="text-xs text-slate-500">المصروفات المدفوعة</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{formatCurrency(totals.expensesPaid)}</div>
          </div>

          <div
            className="rounded-xl border border-slate-200 bg-white p-3"
            title="صافي ربح الشهر = المبيعات المودعة - المصروفات المدفوعة"
          >
            <div className="text-xs text-slate-500">صافي الربح</div>
            <div className="mt-1 text-lg font-semibold text-emerald-700">{formatCurrency(totals.netProfit)}</div>
          </div>

          <div
            className="rounded-xl border border-slate-200 bg-white p-3"
            title="بنود مجدولة داخل الشهر ولم تُنفّذ بعد"
          >
            <div className="text-xs text-slate-500">معلّق داخل الشهر</div>
            <div className="mt-1 text-sm text-slate-800">
              إيداعات: <span className="font-semibold">{formatCurrency(totals.pendingSales)}</span> • مصروفات:{' '}
              <span className="font-semibold">{formatCurrency(totals.pendingExpenses)}</span>
            </div>
          </div>
        </div>
      )}

      {closure && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-slate-600" />
            <span>
              هذا الشهر مُقفَل منذ: <strong>{formatDate(closure.closedAt)}</strong>
              {closure.note ? ` • ملاحظة: ${closure.note}` : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
